/**
 * @component JointCertBizSignDialog
 * @title 공동인증서 일반 전자서명 팝업
 * @description
 *
 * @properties
 *  Props {
 *  }
 * @linked_menu
 * @author 2034263
 * @history
 *   - [2026-03-04] 초기 작성
 */

import 'swiper/css';
import 'swiper/css/pagination';

import { SwiperSlide } from 'swiper/react';

import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AccessibleSwiper,
  CertificationCard,
  CertificationCardInfo,
  cn,
  DialogWrapper,
  FieldRound,
  FieldWrapper,
  useDevice,
} from 'scbk-ui-web';

import type { NCertInfo } from 'scbk-services';
import {
  deviceUtils,
  Keypad,
  NativeBridgeResponse,
  useCertificateBridge,
  useKeypadBridge,
  useMenuRouter,
  useUBIKeyBridge,
} from 'scbk-services';
import {
  certUtils,
  ConnectType,
  dateUtils,
  Dialog,
  SecureKeypadType,
  SignActionType,
  SignVerifyType,
  useGetPublicKey,
  usePageMove,
  useSession,
} from 'scbk-shared-module';

import { useSign } from '../hooks';
import { useCertStore } from '../store';
import type { BizSignParameter, SecureKeypadResponse, SignResult } from '../types';

type ShowCertItem = {
  title: string;
  certIndex?: number;
  list: { title: string; desc?: string }[];
  ubikey?: boolean;
};

export const JointCertBizSignDialog: React.FC<ReturnType<typeof useSign>> = ({
  signPopup,
  signCtx,
  onCancelSign,
  onFailSign,
  onSuccessSign,
}) => {
  const { t } = useTranslation();
  const { setPopupScroll } = useDevice();
  const { doNEncKeyPad } = useKeypadBridge();
  const { doNCertSign, doNCertList } = useCertificateBridge();
  const { doNUbiKeyGetCert } = useUBIKeyBridge();
  const { publicKey } = useGetPublicKey();

  const { isLogin, getUserInfo } = useSession();
  const { goMenu } = useMenuRouter();
  const { goMain } = usePageMove();

  // 보안키패드 응답
  const secureKeypadResRef = useRef<SecureKeypadResponse | null>(null);
  // 인증서 비밀번호 input
  const certCredentialInputRef = useRef<HTMLInputElement>(null);
  // 유효인증서목록 데이터
  const certListRef = useRef<Array<NCertInfo>>([]);

  // 보안키패드 Key
  const [serverKey, setServerKey] = useState<string>('');
  // 선택된 인증서 index
  const [activeCertIndex, setActiveCertIndex] = useState(0);
  // 인증서 목록
  const [certItemList, setCertItemList] = useState<Array<ShowCertItem> | null>(null);
  // 인증서 비밀번호 오류 가이드 메시지
  const [errorHelpMessage, setErrorHelpMessage] = useState<string | undefined>();

  /**
   * 보안키패드 key 초기화
   */
  const initServerKey = useCallback(async () => {
    const nfilterKey = await publicKey();
    setServerKey(encodeURIComponent(nfilterKey));
  }, [publicKey]);

  /**
   * 로그인인증서 혹은 이전 서명 인증서 정보 가져오기
   */
  const getCertInfo = useCallback(async (): Promise<NCertInfo | undefined> => {
    let certInfo: NCertInfo | undefined;

    const userInfo = getUserInfo();
    if (
      userInfo.certInfo &&
      'certificationType' in userInfo.certInfo &&
      userInfo.certInfo.certificationType === 'joint'
    ) {
      certInfo = userInfo.certInfo;
    }

    return certInfo;
  }, []);

  /**
   * 인증서 목록 조회
   */
  const getCertList = useCallback(async (): Promise<NCertInfo[]> => {
    let validCertList: NCertInfo[] | undefined;

    const nativeReq = {
      purpose: signCtx.current?.certListPurpose ?? '0',
    };
    const nativeRes = await doNCertList(nativeReq);
    console.log('doNCertList - nativeRes : ', nativeRes);
    validCertList = nativeRes?.certList;

    if (!validCertList || _.isEmpty(validCertList)) {
      Dialog.alert({
        content: t(
          'Sign.013',
          '인증서 발급이 필요해요. [확인]을 누르면 인증센터에서 발급 후 이용 가능해요.',
        ),
        okBtnLabel: t('Sign.014', '확인'),
        onClick: () => {
          goMenu('CRTJCT000', { historyOptions: { clearHistory: true } }); // 인증센터 - 공동인증서
        },
      });

      throw new Error('유효한 인증서 정보 없음');
    }

    // 만료인증서 필터링
    validCertList = certUtils.getNotExpiredCertList(validCertList);

    if (!validCertList || _.isEmpty(validCertList)) {
      Dialog.alert({
        content: t(
          'Sign.015',
          '보유하고계신 인증서가 만료된 인증서 입니다. 인증서 확인 후 다시 거래하여 주십시오.',
        ),
        onClick: () => {
          goMenu('CRTJCT000', { historyOptions: { clearHistory: true } }); // 인증센터 - 공동인증서
        },
      });

      throw new Error('유효한 인증서 정보 없음');
    }

    return validCertList;
  }, []);

  /**
   * 인증서 목록 Ref 데이터 셋팅
   */
  const setCertListRef = useCallback(async () => {
    const signContext = signCtx?.current;

    if (!signContext || signContext.actionType !== SignActionType.SIGN) {
      onFailSign();
      return;
    }

    let certList: NCertInfo[] | undefined;
    const certInfo = await getCertInfo();

    if (
      !isLogin() || // 비로그인
      getUserInfo().connectType != ConnectType.JOINT_CERT || // 공동인증서 로그인이 아님
      !certInfo || // 인증서 정보 없음
      signContext.isMultiSign || // 다건서명
      signContext.isShowJointCertList // 인증서 목록 노출 요청 여부
    ) {
      certList = await getCertList();
    } else {
      certList = [certInfo];
    }

    if (certList) {
      certListRef.current = certList;
    }
  }, [getCertInfo, getCertList]);

  /**
   * 노출할 인증서 목록 설정
   */
  const showCertItemList = useCallback(async () => {
    // 인증서 정보 목록 셋팅
    await setCertListRef();

    if (!certListRef.current || _.isEmpty(certListRef.current)) {
      setCertItemList(null);
      return;
    }

    const itemList = certListRef.current.map((cert: NCertInfo) => {
      if (deviceUtils.isIOS() && cert.storagetype === 'ubikey') {
        return {
          title: t('Sign.041', '휴대폰인증서 서비스'),
          certIndex: cert.index,
          ubikey: true,
          list: [
            {
              title: t('Sign.042', '휴대폰인증서 서비스 앱(APP)으로 연결돼요.'),
            },
          ],
        };
      } else {
        return {
          title: certUtils.getUserName(cert.user),
          certIndex: cert.index,
          ubikey: cert.storagetype === 'ubikey',
          list: [
            {
              title: t('Sign.016', '발급자'),
              desc: cert.issuer || '',
            },
            {
              title: t('Sign.017', '구분'),
              desc: cert.certType || '',
            },
            {
              title: t('Sign.018', '만료일'),
              desc: dateUtils.getFrmDate(cert.expiredate, '.'),
            },
          ],
        };
      }
    });

    setCertItemList(itemList);
  }, [setCertListRef]);

  // 스크롤 초기화
  const initScroll = useCallback(() => {
    if (!certCredentialInputRef.current) {
      return;
    }

    const scrollParent = certCredentialInputRef.current.closest(
      '.dialog-viewport',
    ) as HTMLElement | null;
    const rect = certCredentialInputRef.current.getBoundingClientRect();

    if (scrollParent) {
      const parentRect = scrollParent.getBoundingClientRect();

      const targetY =
        scrollParent.scrollTop + (rect.top - parentRect.top) - parentRect.height * 0.25;

      setPopupScroll?.(true);
      setTimeout(() => {
        scrollParent.scrollTo({
          top: Math.max(0, targetY),
          behavior: 'smooth',
        });
      }, 300);
    }
  }, []);

  // 스크롤 롤백
  const rollbackScroll = useCallback(() => {
    if (!certCredentialInputRef.current) {
      return;
    }
    // 스크롤 롤백
    const scrollParent = certCredentialInputRef.current.closest(
      '.dialog-viewport',
    ) as HTMLElement | null;
    if (scrollParent) {
      setPopupScroll?.(false);
    }
  }, []);

  /**
   * 서명실행
   */
  const doJointCertBizSign = useCallback(async () => {
    if (!signCtx.current) {
      onFailSign();
      throw new Error('서명 요청 데이터 미존재!');
    }

    const signParameter: BizSignParameter = {
      ...(signCtx.current as BizSignParameter),
    };

    // 선택된 인증서 정보
    const nCertInfo: NCertInfo = certListRef.current[activeCertIndex];

    console.log(
      'JointCertBizSignDialog > doJointCertBizSign - signCtx : ',
      signCtx.current,
      '| secureKeyPadResRef : ',
      secureKeypadResRef.current,
      '| nCertInfo : ',
      nCertInfo,
    );

    if (!certCredentialInputRef.current!.value) {
      Dialog.alert({
        content: t('Sign.019', '인증서 비밀번호를 입력해 주세요.'),
        onClick: () => {
          certCredentialInputRef.current?.focus();
          showSecureKeypad();
        },
      });
      return;
    }

    const nativeReq = {
      storage: certUtils.getStorage(nCertInfo.storagetype),
      type: nCertInfo.type,
      index: nCertInfo.index,
      serial: nCertInfo.serial,
      certCredential: secureKeypadResRef.current?.encData ?? '',
      serverKey: serverKey,
      plainData: signParameter.reqSignData as string,
    };

    console.log('doJointCertBizSign > doJointCertLoginSign - nativeReq : ', nativeReq);

    // Native 서명 호출
    doNCertSign(nativeReq, {
      onError: (error) => {
        // 비밀번호 오류
        if (error.code === '1') {
          // 인증서 에러정보 업데이트
          const { errorCount } = useCertStore.getState().increaseErrorCount(nCertInfo.serial);

          if (errorCount < 5) {
            setErrorHelpMessage(
              `${t('Sign.039', '선택된 인증서 비빌번호가 맞지 않습니다.')}${t('Sign.040', '인증서 비밀번호를 확인 후 다시 입력해 주세요.', { retryRemainingCount: String(5 - errorCount) })}`,
            );
          } else {
            Dialog.alert({
              content: t('Sign.032', '인증서 비밀번호를 5회 잘못 입력했어요. 홈으로 이동할게요.'),
              onClick: () => {
                goMain();
              },
            });
          }
          return;
        }
        // 기타 오류
        else {
          Dialog.alert({
            content: t('Sign.033', '기타 오류가 발생하였습니다.'),
            onClick: () => {},
          });
          return;
        }
      },
      onSuccess: (payload) => {
        // 인증서 에러정보 초기화
        useCertStore.getState().clearErrorCount(nCertInfo.serial);

        console.log('doJointCertBizSign > doNCertSign(onSuccess) - payload : ', payload);

        if (!signParameter.isMultiSign) {
          const signResultData: Partial<SignResult> = {
            signedType: SignVerifyType.JOINT_CERT,
            signOrgData: signParameter.reqSignData,
            signedData: payload?.pkcs7SignedData,
            vidRandom: payload?.randomVid,
          };
          onSuccessSign(signResultData);
        } else {
          const signResultData: Partial<SignResult> = {
            signedType: SignVerifyType.JOINT_CERT,
            edocSignOrgData: signParameter.reqSignData,
            signedData: payload?.pkcs7SignedData,
            vidRandom: payload?.randomVid,
          };
          onSuccessSign(signResultData);
        }
      },
    });
  }, [serverKey, activeCertIndex, onSuccessSign]);

  /**
   * 보안키패드 호출
   */
  const showSecureKeypad = useCallback(async () => {
    if (!deviceUtils.isApp()) {
      onFailSign();
      throw new Error('접근 불가 채널입니다.');
    }

    if (!signCtx.current) {
      onFailSign();
      throw new Error('서명 요청 데이터 미존재!');
    }

    if (!serverKey) {
      console.warn('serverKey 조회중');
      return;
    }

    // 인증서 비밀번호 input 초기화
    const certCredentialInputElement = certCredentialInputRef.current;
    if (!certCredentialInputElement) {
      return;
    }
    certCredentialInputElement.value = '';
    certCredentialInputElement.focus();

    // 선택된 인증서 정보
    const nCertInfo: NCertInfo = certListRef.current[activeCertIndex];

    // IOS인데 ubikey인증서인 경우 인증서 정보 조회
    if (deviceUtils.isIOS() && nCertInfo.storagetype === 'ubikey') {
      const nativeRes = await doNUbiKeyGetCert('1'); // '1' : 전자서명
      console.log('JointCertLoginSignDialog > doNUbiKeyGetCert - nativeRes : ', nativeRes);

      if (nativeRes?.result !== '0') {
        // 사용불가능
        Dialog.alert({ content: t('Sign.038', 'UBIKEY 처리중 오류가 발생하였습니다.') });
        return;
      } else {
        // IOS Ubikey인 경우 serial을 certid로 대체
        certListRef.current[activeCertIndex].serial = nativeRes.certId;
        console.log(
          'JointCertLoginSignDialog > doNUbiKeyGetCert - serial : ',
          certListRef.current[activeCertIndex].serial,
        );
      }
    }

    // 스크롤 초기화
    initScroll();

    // 보안키패드 실행
    doNEncKeyPad(
      {
        id: 'certPassword',
        type: SecureKeypadType.Qwerty,
        serverKey: serverKey,
        maxlength: '30',
        aesEnc: '1',
      },
      (nativeRes: NativeBridgeResponse<Keypad.EncKeypadResponse>) => {
        console.log(
          'JointCertLoginSignDialog > showSecureKeypad > doNEncKeyPad - nativeRes : ',
          nativeRes,
        );

        if (!nativeRes?.payload) {
          console.error('네이티브 보안키패드 응답 미존재');
          return;
        }

        if (nativeRes.payload.result === '-1') {
          // 입력 취소
          secureKeypadResRef.current = null;
          certCredentialInputElement.value = '';
          rollbackScroll();
          certCredentialInputElement.blur();
        } else if (nativeRes.payload.result === '0') {
          // 입력 완료
          secureKeypadResRef.current = { ...nativeRes.payload };
          doJointCertBizSign();
          rollbackScroll();
          certCredentialInputElement.blur();
        } else {
          // 입력 중
          certCredentialInputElement.value = nativeRes.payload.dummyData;
        }
      },
    );
  }, [serverKey, activeCertIndex, doJointCertBizSign]);

  const initPage = useCallback(async () => {
    // 보안키패드 key 초기화
    await initServerKey();

    // 인증서 목록 노출
    showCertItemList();
  }, [initServerKey, showCertItemList]);

  /**
   * 진입처리
   */
  useEffect(() => {
    initPage();
  }, [initPage]);

  return (
    <>
      {certItemList && (
        <DialogWrapper
          title={t('Sign.020', '전자서명')}
          isFull
          open={signPopup.isOpen}
          setOpen={signPopup.setOpen}
          onClose={() => {
            onCancelSign();
          }}
        >
          <AccessibleSwiper
            className="w-full mt-7"
            onSlideChange={(swiper) => {
              setActiveCertIndex(swiper.realIndex);
            }}
          >
            {certItemList.map((item, index) => (
              <SwiperSlide key={`certification-card-${index}`}>
                <div className={cn('[&>button]:w-full')}>
                  <CertificationCard title={item.title} hasArrow={false} ubikey={item.ubikey}>
                    {item.list &&
                      item.list.map((info, idx2) => (
                        <CertificationCardInfo
                          key={`certification-${index}-${idx2}`}
                          title={info.title}
                          desc={info.desc}
                        />
                      ))}
                  </CertificationCard>
                </div>
              </SwiperSlide>
            ))}
          </AccessibleSwiper>
          <div className="mt-6">
            <FieldWrapper
              labelFor="field-text"
              isError={!_.isNil(errorHelpMessage) && !_.isEmpty(errorHelpMessage)}
              helpMessage={errorHelpMessage}
            >
              <FieldRound
                id="field-text"
                aria-label="인증서 비밀번호"
                placeholder={t('Sign.034', '영문/숫자/특수문자 포함 10자리 이상')}
                type="password"
                inputMode="none"
                ref={certCredentialInputRef}
                onFocus={() => {
                  showSecureKeypad();
                }}
                error={!_.isNil(errorHelpMessage) && !_.isEmpty(errorHelpMessage)}
              />
            </FieldWrapper>
          </div>
        </DialogWrapper>
      )}
    </>
  );
};
